import type { AdminCompaniesRosterResponse } from '@/api/(admin)/companies/types';
import type {
  AdminPlatformFeatureResponse,
  AdminPlatformFeaturesResponse,
  PatchPlatformFeatureBody,
} from '@/api/(admin)/platform-features/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Every platform feature. Pass a `companyId` to have each row carry whether
 * that company has it — see `AdminPlatformFeature`.
 */
export async function fetchAdminPlatformFeatures(
  qs: string,
): Promise<AdminPlatformFeaturesResponse> {
  return apiFetch<AdminPlatformFeaturesResponse>(
    `/admin/platform-features?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function patchAdminPlatformFeature(
  id: string,
  body: PatchPlatformFeatureBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/admin/platform-features/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** One feature — the detail screen's header. */
export async function fetchAdminPlatformFeature(
  id: string,
): Promise<AdminPlatformFeatureResponse> {
  return apiFetch<AdminPlatformFeatureResponse>(
    `/admin/platform-features/${id}`,
    {},
    BACKEND_URL,
  );
}

/**
 * Companies that currently hold a feature. Takes a pre-built query string —
 * paging, sort and filter JSON are assembled in the hook.
 *
 * Returns the same envelope as the companies roster: the endpoint reuses the
 * admin list's SELECT and the unified `buildPaginationMetadata` envelope. The
 * route moved off `admin/companies` onto this resource — it filters on a
 * platform feature, not a company — as part of the admin-companies REST
 * redesign.
 */
export async function fetchCompaniesByPlatformFeatureId(
  featureId: string,
  qs: string,
): Promise<AdminCompaniesRosterResponse> {
  return apiFetch<AdminCompaniesRosterResponse>(
    `/admin/platform-features/${featureId}/companies?${qs}`,
    {},
    BACKEND_URL,
  );
}
